import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Edit, Trash2, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

export default function AdminBlogList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const res = await adminApi.listBlog({ status, q });
    if (res.ok) setItems(res.data.items);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    const res = await adminApi.deleteBlog(id);
    if (res.ok) load();
    else alert(res.error);
  };

  return (
    <div data-testid="admin-blog-list">
      <header className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="overline">Content</span>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">Blog Posts</h1>
        </div>
        <Link to="/admin/blog/new" className="btn-accent" data-testid="admin-blog-new">
          <Plus className="h-4 w-4" /> New Post
        </Link>
      </header>

      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="mt-8 flex flex-col gap-3 rounded-md border border-ridge-border bg-white p-4 sm:flex-row"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ridge-muted" />
          <input
            type="search"
            placeholder="Search title, excerpt or tag…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-blog-search"
            className="w-full rounded-md border border-ridge-border py-2.5 pl-9 pr-3 text-sm focus:border-ridge-charcoal focus:outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="admin-blog-status-filter"
          className="rounded-md border border-ridge-border px-3 py-2.5 text-sm focus:border-ridge-charcoal focus:outline-none sm:w-44"
        >
          <option value="">All</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
        <button type="submit" className="btn-primary px-5 py-2 text-xs">Apply</button>
      </form>

      <div className="mt-6 overflow-hidden rounded-md border border-ridge-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ridge-stone/60 text-xs uppercase tracking-[0.14em] text-ridge-muted">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Title</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
              <th className="px-4 py-3 text-left font-semibold">Updated</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-ridge-border">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-ridge-muted">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-ridge-muted">No blog posts yet. Create your first one.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="hover:bg-ridge-stone/40" data-testid={`admin-blog-row-${p.id}`}>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/blog/${p.id}`)}
                    className="block text-left font-semibold text-ridge-charcoal hover:text-ridge-amber"
                  >
                    {p.title}
                  </button>
                  {p.excerpt ? <p className="mt-0.5 line-clamp-1 text-xs text-ridge-muted">{p.excerpt}</p> : null}
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-md px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] ${
                    p.status === "published" ? "bg-green-100 text-green-800" : "bg-zinc-200 text-zinc-700"
                  }`}>{p.status}</span>
                </td>
                <td className="px-4 py-3 text-ridge-muted">{(p.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-3 text-xs">
                    {p.status === "published" ? (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-ridge-amber hover:text-ridge-amber-dark" title="View live">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : null}
                    <Link to={`/admin/blog/${p.id}`} className="text-ridge-charcoal hover:text-ridge-amber" title="Edit">
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button type="button" onClick={() => onDelete(p.id, p.title)} className="text-red-600 hover:text-red-800" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
